import { useEffect, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { v4 as uuidv4 } from 'uuid';
import useTranslation from '../../../../../hooks/useTranslations';
import { selectBusinessAreasFilters } from '../../../../../redux/modules/data/newsSlice';
import { getLinkOfObject, selectAllObjects } from '../../../../../redux/modules/data/objectsSlice';
import ConditionalWrapper from '../../../../misc/ConditionalWrapper';
import { AlertIcon, LinkIcon, MailIcon } from '../../../../misc/icons/Icons';
import useFormatLocaleDate from '../../../../../hooks/useFormatLocaleDate';

const NewsListRow = ({ news, isDetail = false }) => {
	const t = useTranslation();
	const objects = useSelector(selectAllObjects);
	const [newsLink, setNewsLink] = useState(null);

	// TODO: Convert navigation links --> oly-msd-eu://?businessareaId=[businessareaId]&navigationId=[navigationId]
	const getNewsLink = (link) => {
		if (link) {
			if (link.startsWith('http') || link.startsWith('mailto')) {
				return link;
			}
			if (link.startsWith('msd://') || link.startsWith('oly-msd-eu')) {
				const objectId = link.substring(link.indexOf('://') + '://'.length);
				if (objects?.[objectId]) {
					const url = getLinkOfObject(objects?.[objectId]);
					if (url) {
						return url;
					}
				}
			}
		}
		return null;
	};

	useEffect(() => {
		const link = getNewsLink(news?.image_link);
		if (link) {
			setNewsLink(link);
		}
		/* eslint-disable-next-line */
	}, []);

	const captureClick = (e) => {
		if (e?.target instanceof HTMLAnchorElement) {
			e.preventDefault();
			const link = getNewsLink(e?.target?.href);
			if (link) {
				window.open(link, '_blank');
			}
		}
	};

	const allBusinessAreaFilters = useSelector((state) => selectBusinessAreasFilters(state));
	let sortedBusinessAreas = [];
	if (news?.business_areas) {
		if (news.business_areas.length === allBusinessAreaFilters.length - 1 && news.business_areas.length > 3) {
			sortedBusinessAreas = [{ id: 1, translationkey: 'webapp_news_filter_bas_all' }];
		} else {
			const businessAreasForSort = [...news.business_areas];
			sortedBusinessAreas = businessAreasForSort.sort((a, b) => t(a.translationkey).localeCompare(t(b.translationkey)));
		}
	}

	let sortedNewsCategories = [];
	if (news?.news_categories) {
		const newsCategoriesForSort = [...news.news_categories];
		sortedNewsCategories = newsCategoriesForSort.sort((a, b) => t(a.translationkey).localeCompare(t(b.translationkey)));
	}

	const getNewsSliderImages = () => {
		const imageSliderImages = [];
		if (news.file_bigimage) {
			imageSliderImages.push(news.file_bigimage);
		}
		if (news.file_slide2) {
			imageSliderImages.push(news.file_slide2);
		}
		if (news.file_slide3) {
			imageSliderImages.push(news.file_slide3);
		}
		if (news.file_slide4) {
			imageSliderImages.push(news.file_slide4);
		}
		if (news.file_slide5) {
			imageSliderImages.push(news.file_slide5);
		}
		return imageSliderImages;
	};

	return (
		<div className={`relative flex items-center flex-col w-full ${isDetail ? 'mb-5 sm:mb-0' : 'can-hover:hover:bg-gray-50'}`}>
			<ConditionalWrapper
				condition={!isDetail}
				wrapper={(children) => <NavLink className="w-full" to={`/news/${news.id}`}>{children}</NavLink>}
			>
				<div className="flex flex-col items-center justify-start flex-grow w-full h-full p-5 text-left sm:flex-row focus:outline-none">
					<div className={`relative flex flex-shrink-0 order-2 w-full mb-auto mt-3 sm:mt-0 sm:mr-[5%] sm:h-48 sm:w-1/3 xl:w-60 sm:order-1 ${isDetail && 'hidden sm:flex'}`}>
						<LazyLoadImage
							alt={news.title}
							src={news.file ? `${process.env.REACT_APP_API_BASE_URL}/${news.file}` : `${process.env.PUBLIC_URL}/images/placeholder.png`}
							placeholderSrc={`${process.env.PUBLIC_URL}/images/placeholder.png`}
							className="object-contain object-top w-full h-full max-h-56 sm:max-h-full"
							wrapperClassName="w-full !bg-no-repeat !bg-contain !bg-center"
						/>
					</div>
					<div className="flex flex-col self-start order-1 h-full mr-14 sm:order-2 sm:mr-20">
						<div className="flex flex-wrap mb-4 gap-x-2 gap-y-2">
							{sortedBusinessAreas.map((businessArea) => <Badge key={businessArea?.id} title={t(businessArea?.translationkey)} />)}
							{sortedNewsCategories.map((newsCategory) => <Badge key={newsCategory?.id} title={t(newsCategory.title)} isNewsCat />)}
						</div>
						{isDetail && (
							<div className="relative flex flex-shrink-0 w-full my-4 pl-14 sm:hidden">
								<LazyLoadImage
									alt={news.title}
									src={news.file ? `${process.env.REACT_APP_API_BASE_URL}/${news.file}` : `${process.env.PUBLIC_URL}/images/placeholder.png`}
									placeholderSrc={`${process.env.PUBLIC_URL}/images/placeholder.png`}
									className="object-contain object-top w-full h-full max-h-56"
									wrapperClassName="w-full !bg-no-repeat !bg-contain !bg-center"
								/>
							</div>
						)}
						<span className={`text-[1.25rem] text-oly-blue-dark leading-6 sm:text-[1.5rem] sm:leading-7 ${isDetail ? 'my-4 sm:my-6' : 'mb-2 line-clamp-3 sm:line-clamp-2'}`}>
							{news.title}
						</span>
						<div className={`${isDetail ? 'mb-8 sm:mb-0' : 'overflow-hidden col-count-1 max-h-20'}`}>
							{/* TODO: More style needed? see html-content */}
							{/* eslint-disable-next-line */}
							<span className="html-content text-[0.9375rem] sm:text-[1.0625rem] text-oly-gray-darkest" dangerouslySetInnerHTML={{ __html: news.text }} onClick={(e) => captureClick(e)} />
							{/* TODO: Unwanted behaviour of news.text: oly-msd-eu-dev://?businessareaId=40&amp;navigationId=540 --> oly-msd-eu-dev://my-sales-docs.mmp.de?businessareaId=40&amp;navigationId=540 */}
						</div>
					</div>
				</div>
			</ConditionalWrapper>
			{isDetail && getNewsSliderImages().length > 0 && (
				<div className="flex justify-center max-w-screen-lg m-5 sm:m-16">
					{getNewsSliderImages().length > 1 && (
						<Splide
							options={{
								type: 'loop',
								width: 665,
							}}
						>
							{getNewsSliderImages().map((slide) => (
								<SplideSlide key={uuidv4()}>
									<span className="flex items-center h-full mx-auto sm:w-[555px]">
										<img
											alt={news.title}
											src={`${process.env.REACT_APP_API_BASE_URL}/${slide}`}
										/>
									</span>
								</SplideSlide>
							))}
						</Splide>
					)}
					{getNewsSliderImages().length === 1 && (
						getNewsSliderImages().map((slide) => (
							<LazyLoadImage
								alt={news.title}
								src={`${process.env.REACT_APP_API_BASE_URL}/${slide}`}
								placeholderSrc={`${process.env.PUBLIC_URL}/images/placeholder.png`}
								className="w-full"
								wrapperClassName="lg:w-[555px]"
							/>
						))
					)}
				</div>
			)}
			<div className="absolute right-0 flex flex-col items-center flex-shrink-0 p-3 sm:gap-2 sm:p-5">
				<span className="text-[0.8125rem] line-clamp-1 sm:text-[0.9375rem] text-oly-gray-dark">
					{useFormatLocaleDate(new Date(news.date))}
				</span>
				{news?.urgent === '1' && (
					<div className="flex items-center p-2 text-oly-red-default">
						<AlertIcon className="w-6 h-6 sm:w-8 sm:h-8" />
					</div>
				)}
				{news?.email && news?.email !== '' && (
					<a
						href={`mailto:${news.email}?subject=${news.title}`}
						title={t('register_mail_placeholder')}
						className="flex items-center w-10 h-10 p-2 rounded-lg text-oly-blue-dark sm:w-12 sm:h-12 can-hover:hover:shadow-button focus-visible:shadow-button focus:outline-none"
					>
						<MailIcon />
					</a>
				)}
				{newsLink && (
					<a
						href={newsLink}
						target="_blank"
						title={newsLink}
						className="flex items-center w-10 h-10 p-2 rounded-lg text-oly-blue-dark sm:w-12 sm:h-12 can-hover:hover:shadow-button focus-visible:shadow-button focus:outline-none"
						rel="noreferrer"
					>
						<LinkIcon />
					</a>
				)}
			</div>
		</div>
	);
};

const Badge = ({ title, isNewsCat }) => (
	<span className={`text-[0.75rem] line-clamp-1 px-3 leading-5 rounded-full ${isNewsCat ? 'text-oly-gray-darkest bg-oly-gray-lightest' : 'text-white bg-oly-yellow-default'}`}>
		{title}
	</span>
);

export default NewsListRow;
